import { useEffect, useState } from 'react';
import { supabase } from '../supabase';

export interface RadarValues {
  tech: number;
  phys: number;
  psych: number;
  social: number;
}

const FALLBACK: RadarValues = { tech: 0, phys: 0, psych: 0, social: 0 };

const CORNER_KEYS: Array<{ key: keyof RadarValues; matches: (id: string) => boolean }> = [
  { key: 'tech', matches: (id) => /tech|technical/i.test(id) },
  { key: 'phys', matches: (id) => /phys/i.test(id) },
  { key: 'psych', matches: (id) => /psych/i.test(id) },
  { key: 'social', matches: (id) => /social/i.test(id) },
];

/**
 * Mean rating per corner over the last 28 days for a team.
 * Reads from `ratings` table directly. Falls back to zeros if RLS or schema
 * disagrees — Home's radar still renders, just empty.
 */
export function useTeamRadar(teamId: string | undefined) {
  const [values, setValues] = useState<RadarValues>(FALLBACK);

  useEffect(() => {
    if (!teamId) return;
    let cancelled = false;
    (async () => {
      const since = new Date();
      since.setDate(since.getDate() - 28);
      // ratings.ratee_id -> users.id; the team membership is on
      // team_players (no standalone players table). So look up the team's
      // player_ids first, then fetch ratings for that set. Two cheap
      // round-trips instead of an embedded relationship that doesn't exist.
      const { data: roster } = await supabase
        .from('team_players')
        .select('player_id')
        .eq('team_id', teamId)
        .eq('is_active', true);
      if (cancelled) return;
      const playerIds = (roster ?? []).map((r: any) => r.player_id as string);
      if (playerIds.length === 0) { setValues(FALLBACK); return; }

      const { data, error } = await supabase
        .from('ratings')
        .select('score, corner_id, ratee_id')
        .in('ratee_id', playerIds)
        .gte('created_at', since.toISOString());

      if (cancelled) return;
      if (error || !data) { setValues(FALLBACK); return; }

      const sums: Record<keyof RadarValues, { total: number; count: number }> = {
        tech: { total: 0, count: 0 },
        phys: { total: 0, count: 0 },
        psych: { total: 0, count: 0 },
        social: { total: 0, count: 0 },
      };

      for (const row of data) {
        const cornerId = String(row.corner_id ?? '');
        const score = Number(row.score ?? 0);
        const match = CORNER_KEYS.find((k) => k.matches(cornerId));
        if (match) {
          sums[match.key].total += score;
          sums[match.key].count += 1;
        }
      }

      setValues({
        tech: sums.tech.count ? Math.round(sums.tech.total / sums.tech.count) : 0,
        phys: sums.phys.count ? Math.round(sums.phys.total / sums.phys.count) : 0,
        psych: sums.psych.count ? Math.round(sums.psych.total / sums.psych.count) : 0,
        social: sums.social.count ? Math.round(sums.social.total / sums.social.count) : 0,
      });
    })();
    return () => { cancelled = true; };
  }, [teamId]);

  return values;
}
